/**
 * Ajaxinate - Shopify infinite scroll plugin
 * https://github.com/Elkfox/Ajaxinate
 * 
 * Automatically loads more products as you scroll or click
 */
class Ajaxinate {
  constructor(config) {
    // Default settings
    const settings = config || {};
    
    this.settings = {
      container: settings.container || '#AjaxinateContainer',
      pagination: settings.pagination || '#AjaxinatePagination',
      method: settings.method || 'scroll',
      offset: settings.offset || 0,
      loadingText: settings.loadingText || 'Loading',
      callback: settings.callback || null
    };

    // Set up elements
    this.containerElement = document.querySelector(this.settings.container);
    this.paginationElement = document.querySelector(this.settings.pagination);
    
    // Validate elements exist
    if (!this.containerElement) {
      console.warn('Ajaxinate: Container element not found');
      return;
    }
    
    if (!this.paginationElement) {
      console.warn('Ajaxinate: Pagination element not found');
      return;
    }

    // Store event handlers for cleanup
    this.scrollHandler = null;
    this.clickHandler = null;

    this.initialize();
  }

  initialize() {
    // Get the next page link
    this.nextPageUrl = this.getNextPageUrl();
    
    if (!this.nextPageUrl) {
      // No more pages to load
      return;
    }

    // Set up based on method
    if (this.settings.method === 'click') {
      this.initializeClick();
    } else {
      this.initializeScroll();
    }
  }

  getNextPageUrl() {
    const nextLink = this.paginationElement.querySelector('a[rel="next"]');
    return nextLink ? nextLink.href : null;
  }

  initializeClick() {
    const nextLink = this.paginationElement.querySelector('a[rel="next"]');
    
    if (!nextLink) return;

    // Store click handler for later cleanup
    this.clickHandler = (e) => {
      e.preventDefault();
      this.loadMore();
    };
    
    nextLink.addEventListener('click', this.clickHandler);
  }

  initializeScroll() {
    let isLoading = false;
    
    const checkScroll = () => {
      if (isLoading) return;

      // Check if pagination element still exists
      if (!this.paginationElement) return;

      const paginationRect = this.paginationElement.getBoundingClientRect();
      const threshold = window.innerHeight + this.settings.offset;

      // Check if pagination is in viewport
      if (paginationRect.top < threshold) {
        isLoading = true;
        this.loadMore();
      }
    };

    // Listen to scroll events with throttling
    let scrollTimeout;
    this.scrollHandler = () => {
      if (scrollTimeout) {
        window.cancelAnimationFrame(scrollTimeout);
      }
      scrollTimeout = window.requestAnimationFrame(() => {
        checkScroll();
      });
    };
    
    window.addEventListener('scroll', this.scrollHandler);

    // Initial check
    checkScroll();
  }

  loadMore() {
    if (!this.nextPageUrl) return;

    // Show loading state
    this.showLoadingState();

    // Fetch the next page
    fetch(this.nextPageUrl)
      .then(response => response.text())
      .then(data => {
        this.handleResponse(data);
      })
      .catch(error => {
        console.error('Ajaxinate: Error loading more items', error);
        this.hideLoadingState();
      });
  }

  showLoadingState() {
    const loadingText = this.settings.loadingText;
    this.paginationElement.innerHTML = `<span class="ajaxinate-loading">${loadingText}...</span>`;
  }

  hideLoadingState() {
    const nextLink = this.paginationElement.querySelector('a[rel="next"]');
    if (!nextLink) {
      this.paginationElement.style.display = 'none';
    }
  }

  handleResponse(html) {
    // Parse the HTML response
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Get the new products
    const newContainer = doc.querySelector(this.settings.container);
    const newPagination = doc.querySelector(this.settings.pagination);

    if (newContainer) {
      // Append new products to container
      const newItems = Array.from(newContainer.children);
      newItems.forEach(item => {
        this.containerElement.appendChild(item);
      });
    }

    // Update pagination
    if (newPagination) {
      this.paginationElement.innerHTML = newPagination.innerHTML;
      
      // Get next page URL for next load
      this.nextPageUrl = this.getNextPageUrl();
      
      // Reinitialize for next page
      if (this.nextPageUrl) {
        this.initialize();
      }
    } else {
      // No more pages
      this.paginationElement.style.display = 'none';
    }

    // Run callback if provided
    if (typeof this.settings.callback === 'function') {
      this.settings.callback();
    }
  }

  // Method to manually destroy the instance
  destroy() {
    // Remove scroll event listener if it exists
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      this.scrollHandler = null;
    }
    
    // Remove click event listener if it exists
    if (this.clickHandler && this.paginationElement) {
      const nextLink = this.paginationElement.querySelector('a[rel="next"]');
      if (nextLink) {
        nextLink.removeEventListener('click', this.clickHandler);
      }
      this.clickHandler = null;
    }
    
    // Clear references
    this.containerElement = null;
    this.paginationElement = null;
    this.nextPageUrl = null;
    
    console.log('Ajaxinate: Instance destroyed');
  }
}

// Export for use in Shopify
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Ajaxinate;
}

